import type { IconName, HighlightIconVariant } from '@dev-spendesk/grapes';

import type { TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import {
  type ActivityType,
  type PhysicalCardLoad,
  type PhysicalCardLoadActivityType,
} from 'modules/cards/hooks/useCardAggregateActivity';

export function getIconPropertiesForCardActivity({
  activityType,
  isSubscription,
}: {
  activityType: ActivityType | PhysicalCardLoadActivityType;
  isSubscription?: boolean;
}): { icon: IconName; variant: HighlightIconVariant } | undefined {
  if (activityType === 'blocked') {
    return isSubscription
      ? {
          icon: 'circle-pause',
          variant: 'ocean',
        }
      : {
          icon: 'lock-close',
          variant: 'peach',
        };
  }
  if (activityType === 'unblocked') {
    return isSubscription
      ? {
          icon: 'circle-play',
          variant: 'lemon',
        }
      : {
          icon: 'lock-open',
          variant: 'emerald',
        };
  }
  if (activityType === 'top-up') {
    return {
      icon: 'card-arrow-up',
      variant: 'forest',
    };
  }
  if (activityType === 'top-down') {
    return {
      icon: 'card-arrow-down',
      variant: 'apricot',
    };
  }
  if (['ordered', 'sms-pin-sent'].includes(activityType)) {
    return {
      icon: 'paper-plane',
      variant: 'blue',
    };
  }
  if (activityType === 'activated') {
    return {
      icon: 'circle-check',
      variant: 'emerald',
    };
  }

  return {
    icon: 'pen',
    variant: 'pink',
  };
}

export function getCardLoadActivityType(
  cardLoad: PhysicalCardLoad,
): 'top-up' | 'top-down' | 'unknown' {
  if (!cardLoad.amount) {
    return 'unknown';
  }
  return cardLoad.amount > 0 ? 'top-up' : 'top-down';
}

function getLoadedBy(
  translator: TGlobalFunctionTyped,
  {
    cardLoad,
  }: {
    cardLoad: PhysicalCardLoad;
  },
) {
  if (cardLoad.auto_approved) {
    return translator('cards.activity.feed.topUpByAuto', {
      name: cardLoad.user.fullname,
    });
  }
  if (!cardLoad.approvers || cardLoad.approvers.length === 0) {
    return translator('cards.activity.feed.topUpBy', {
      name: cardLoad.user.fullname,
    });
  }
  const approversNames = cardLoad.approvers
    .map((approver) => approver.fullname)
    .join(', ');
  return translator('cards.activity.feed.topUpApprovedBy', {
    name: approversNames,
  });
}

export function getCardLoadActivityText(
  translator: TGlobalFunctionTyped,
  {
    cardLoad,
    cardLoadActivityType,
  }: {
    cardLoad: PhysicalCardLoad;
    cardLoadActivityType: 'top-up' | 'top-down' | 'unknown';
  },
) {
  if (cardLoadActivityType === 'top-down') {
    return translator('cards.activity.feed.unloadedBy', {
      name: cardLoad.user.fullname,
    });
  }
  if (cardLoadActivityType === 'top-up') {
    return getLoadedBy(translator, { cardLoad });
  }
  return translator('misc.na');
}
